// Background Layer Component
// Component to display background image with fallback color and smooth transitions

import { useEffect, useState, useRef } from 'react';
import type { BackgroundConfig } from '../types/vfxConfig';

interface BackgroundLayerProps {
  config: BackgroundConfig | null;
}

export default function BackgroundLayer({ config }: BackgroundLayerProps) {
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const previousUrlRef = useRef<string | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!config || !config.enabled) {
      setCurrentUrl(null);
      setImageError(false);
      return;
    }

    // If URL is null, use fallback color only
    if (!config.url || config.url === null) {
      setCurrentUrl(null);
      setImageError(false);
      setIsLoading(false);
      previousUrlRef.current = null;
      return;
    }

    // If URL hasn't changed, don't reload
    if (config.url === previousUrlRef.current && currentUrl === config.url) {
      return;
    }

    // Preload image before displaying
    setIsLoading(true);
    setImageError(false);
    previousUrlRef.current = config.url;

    const img = new Image();

    img.onload = () => {
      setIsLoading(false);
      setCurrentUrl(config.url);
      setImageError(false);
    };
    img.onerror = () => {
      setIsLoading(false);
      setImageError(true);
      setCurrentUrl(null);
    };
    img.src = config.url;
    imageRef.current = img;
  }, [config, currentUrl]);

  // Get background color (fallback or transparent if disabled)
  const backgroundColor = config?.enabled && config?.fallback
    ? config.fallback
    : 'transparent';

  // Get background image URL (only if loaded successfully)
  const backgroundImage = currentUrl && !imageError && !isLoading
    ? `url(${currentUrl})`
    : 'none';

  return (
    <>
      {/* Background Image Layer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          minWidth: '100%',
          minHeight: '100%',
          zIndex: -1,
          backgroundColor,
          backgroundImage,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          transition: 'background-color 0.5s ease, background-image 0.5s ease',
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      />

      {/* Overlay to blend with UI */}
      {config?.enabled && backgroundImage !== 'none' && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'none',
            transition: 'background-color 0.5s ease',
          }}
          aria-hidden="true"
        />
      )}
    </>
  );
}
